import { useAssertedParams } from '@folio/lib';
import { CustomerFunnelModule } from '@folio/modules';
import { zodResolver } from '@hookform/resolvers/zod';
import { clientWithFork, useCustomerControllerEditCustomer } from 'korint-sdk';
import type { ConditionalVisibility, FormSectionsWithShortcuts } from 'korint-sdk/react';
import { Building2, Hash, Home, MapPin } from 'lucide-react';
import { z } from 'zod';

const showWhenSecondaryResidence = [
  {
    fieldName: 'hasSecondaryResidence',
    condition: 'equals',
    value: 'true',
  },
] satisfies ConditionalVisibility[];

const sections = [
  {
    title: 'Résidence secondaire',
    fields: [
      {
        name: 'hasSecondaryResidence',
        required: false,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: Home,
        isCustomField: false,
        ignoreInPayload: true,
        apiDataAccessor: customer =>
          customer.customFields?.contactSecondaryAddress1 ||
          customer.customFields?.contactSecondaryAddress2
            ? 'true'
            : 'false',
      },
    ],
  },
  {
    title: 'Résidence secondaire 1',
    fields: [
      {
        name: 'contactSecondaryAddress1',
        required: false,
        type: 'text',
        icon: MapPin,
        DEPRECATED_placeholder: '12 rue de la Paix',
        isCustomField: true,
        formDataAccessor: (formData: Record<string, unknown>) => {
          if (formData.contactSecondaryAddress1 === null) return '';
          return formData.contactSecondaryAddress1;
        },
      },
      {
        name: 'contactSecondaryPostCode1',
        required: false,
        type: 'postcode',
        colSpan: 4 as const,
        icon: Hash,
        DEPRECATED_placeholder: '75001',
        isCustomField: true,
        formDataAccessor: (formData: Record<string, unknown>) => {
          if (formData.contactSecondaryPostCode1 === null) return '';
          return formData.contactSecondaryPostCode1;
        },
      },
      {
        name: 'contactSecondaryCity1',
        required: false,
        type: 'text',
        colSpan: 8 as const,
        icon: Building2,
        DEPRECATED_placeholder: 'Paris',
        isCustomField: true,
        formDataAccessor: (formData: Record<string, unknown>) => {
          if (formData.contactSecondaryCity1 === null) return '';
          return formData.contactSecondaryCity1;
        },
      },
    ],
    showWhen: showWhenSecondaryResidence,
  },
  {
    title: 'Résidence secondaire 2',
    fields: [
      {
        name: 'contactSecondaryAddress2',
        required: false,
        type: 'text',
        icon: MapPin,
        DEPRECATED_placeholder: '5 avenue Foch',
        isCustomField: true,
        formDataAccessor: (formData: Record<string, unknown>) => {
          if (formData.contactSecondaryAddress2 === null) return '';
          return formData.contactSecondaryAddress2;
        },
      },
      {
        name: 'contactSecondaryPostCode2',
        required: false,
        type: 'postcode',
        colSpan: 4 as const,
        icon: Hash,
        DEPRECATED_placeholder: '06000',
        isCustomField: true,
        formDataAccessor: (formData: Record<string, unknown>) => {
          if (formData.contactSecondaryPostCode2 === null) return '';
          return formData.contactSecondaryPostCode2;
        },
      },
      {
        name: 'contactSecondaryCity2',
        required: false,
        type: 'text',
        colSpan: 8 as const,
        icon: Building2,
        DEPRECATED_placeholder: 'Nice',
        isCustomField: true,
        formDataAccessor: (formData: Record<string, unknown>) => {
          if (formData.contactSecondaryCity2 === null) return '';
          return formData.contactSecondaryCity2;
        },
      },
    ],
    showWhen: showWhenSecondaryResidence,
  },
] as const satisfies FormSectionsWithShortcuts;

const residenceValidationSchema = z
  .object({
    hasSecondaryResidence: z.string().optional(),
    contactSecondaryAddress1: z.string().optional(),
    contactSecondaryPostCode1: z.string().optional(),
    contactSecondaryCity1: z.string().optional(),
    contactSecondaryAddress2: z.string().optional(),
    contactSecondaryPostCode2: z.string().optional(),
    contactSecondaryCity2: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    const hasResidence1Data =
      data.contactSecondaryAddress1?.trim() ||
      data.contactSecondaryPostCode1?.trim() ||
      data.contactSecondaryCity1?.trim();
    if (hasResidence1Data) {
      if (!data.contactSecondaryAddress1?.trim()) {
        ctx.addIssue({
          code: 'custom',
          path: ['contactSecondaryAddress1'],
          message:
            'Si vous remplissez un champ de la résidence secondaire 1, tous les champs doivent être remplis',
        });
      }
      if (!data.contactSecondaryPostCode1?.trim()) {
        ctx.addIssue({
          code: 'custom',
          path: ['contactSecondaryPostCode1'],
          message:
            'Si vous remplissez un champ de la résidence secondaire 1, tous les champs doivent être remplis',
        });
      }
      if (!data.contactSecondaryCity1?.trim()) {
        ctx.addIssue({
          code: 'custom',
          path: ['contactSecondaryCity1'],
          message:
            'Si vous remplissez un champ de la résidence secondaire 1, tous les champs doivent être remplis',
        });
      }
    }

    const hasResidence2Data =
      data.contactSecondaryAddress2?.trim() ||
      data.contactSecondaryPostCode2?.trim() ||
      data.contactSecondaryCity2?.trim();
    if (hasResidence2Data) {
      if (!data.contactSecondaryAddress2?.trim()) {
        ctx.addIssue({
          code: 'custom',
          path: ['contactSecondaryAddress2'],
          message:
            'Si vous remplissez un champ de la résidence secondaire 2, tous les champs doivent être remplis',
        });
      }
      if (!data.contactSecondaryPostCode2?.trim()) {
        ctx.addIssue({
          code: 'custom',
          path: ['contactSecondaryPostCode2'],
          message:
            'Si vous remplissez un champ de la résidence secondaire 2, tous les champs doivent être remplis',
        });
      }
      if (!data.contactSecondaryCity2?.trim()) {
        ctx.addIssue({
          code: 'custom',
          path: ['contactSecondaryCity2'],
          message:
            'Si vous remplissez un champ de la résidence secondaire 2, tous les champs doivent être remplis',
        });
      }
    }
  });

/**
 * @debt can likely be moved to uniq config, setting the fields required and using showWhen instead of dynamic field array
 */
export function NewBusinessBrokerSecondaryResidenceProtectionJuridique() {
  const { customerId, branchId } = useAssertedParams();

  const { mutateAsync: updateCustomer } = useCustomerControllerEditCustomer({
    client: { client: clientWithFork },
  });

  const afterSubmit = async (allFormValues: Record<string, unknown>) => {
    if (
      allFormValues.hasSecondaryResidence === 'false' ||
      allFormValues.hasSecondaryResidence === false
    ) {
      await updateCustomer({
        customerId,
        params: { branchId },
        data: {
          customFields: [
            { key: 'contactSecondaryAddress1', value: '' },
            { key: 'contactSecondaryPostCode1', value: '' },
            { key: 'contactSecondaryCity1', value: '' },
            { key: 'contactSecondaryAddress2', value: '' },
            { key: 'contactSecondaryPostCode2', value: '' },
            { key: 'contactSecondaryCity2', value: '' },
          ],
        },
      });
    }
  };

  return (
    <CustomerFunnelModule
      sections={sections}
      path="residence"
      mode="stepper"
      customResolver={zodResolver(residenceValidationSchema)}
      afterSubmit={afterSubmit}
    />
  );
}
